<template>
  <div>
    <v-row class="py-0 pr-3">
      <v-col cols="3">
        <v-card max-width="300">
          <v-list shaped>
            <v-subheader class="font-italic text-subtitle-1">Pending Plays</v-subheader>
            <v-divider></v-divider>
            <v-list-item-group color="dw_green">
              <v-list-item v-if="pendingPlays.length === 0" disabled>
                <v-list-item-content>
                  <v-list-item-title>No plays to review</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                v-else
                v-for="(play, i) in pendingPlays"
                :key="i"
                @click="reviewPlay(play)"
                two-line
                dense>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-regular">
                    {{ play.pending_state.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="caption grey--text">
                    {{ pendingState(play.action) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-card>
      </v-col>
      <v-col v-if="selectedPlay" cols="9">
        <v-form ref="reviewPlayForm" v-model="formValid" @submit.prevent>
          <v-row>
            <v-col :cols="selectedPlay.action !== 'UPD' ? 12 : 6" class="py-0">
              <v-card flat outlined>
                <v-card-title class="py-2 text-h6 font-weight-light">
                  Suggested Play
                  <v-spacer></v-spacer>
                  <span class="text-body-2">{{ selectedPlay.submitted_by }}</span>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="px-4 py-0">
                  <moderate-text-field
                    ref="modNameField"
                    :existing="selectedPlay.pending_state.name"
                    label="Play Name *"
                    @saveChanges="(e) => editPlay('name', e)" />
                  <moderate-text-area
                    ref="modDescField"
                    :existing="selectedPlay.pending_state.description"
                    label="Description *"
                    :isRequired="false"
                    @saveChanges="(e) => editPlay('description', e)" />
                  <moderate-select-chips
                    ref="modCAPECsField"
                    :existingIDs="selectedPlay.pending_state.attackpatterns"
                    :options="capecOptions"
                    label="CAPEC&trade; Attack Patterns"
                    prefix="CAPEC-"
                    shorttext="id"
                    longtext="display_name"
                    @saveChanges="(e) => editPlay('attackpatterns', e)" />
                  <moderate-select-chips
                    ref="modTeqsField"
                    :existingIDs="selectedPlay.pending_state.techniques"
                    :options="techniqueOptions"
                    label="MITRE ATT&amp;CK&reg; Techniques"
                    shorttext="mitre_id"
                    longtext="display_name"
                    @saveChanges="(e) => editPlay('techniques', e)" />
                  <moderate-select-chips
                    ref="modCtrlsField"
                    :existingIDs="selectedPlay.pending_state.controls"
                    :options="controlOptions"
                    label="NIST SP 800-53 (Rev. 5) Controls"
                    shorttext="label"
                    longtext="display_name"
                    @saveChanges="(e) => editPlay('controls', e)" />
                  <moderate-select-chips
                    ref="modCWEsField"
                    :existingIDs="selectedPlay.pending_state.cwes"
                    :options="cweOptions"
                    prefix="CWE-"
                    label="CWE&trade; Weaknesses"
                    shorttext="id"
                    longtext="display_name"
                    @saveChanges="(e) => editPlay('cwes', e)" />
                  <moderate-select-chips
                    ref="modToolsField"
                    :existingIDs="selectedPlay.pending_state.tools"
                    :options="toolOptions"
                    label="Tools"
                    shorttext="name"
                    longtext="name"
                    @saveChanges="(e) => editPlay('tools', e)" />
                  <moderate-select-chips
                    ref="modTagsField"
                    :existingIDs="selectedPlay.pending_state.tags"
                    :options="tagOptions"
                    label="Tags"
                    shorttext="name"
                    longtext="name"
                    @saveChanges="(e) => editPlay('tags', e)" />
                  <moderate-select-chips
                    ref="modObjctvsField"
                    :existingIDs="selectedPlay.pending_state.objectives"
                    :options="objectiveOptions"
                    label="Objectives"
                    shorttext="title"
                    longtext="title"
                    @saveChanges="(e) => editPlay('objectives', e)" />
                </v-card-text>
              </v-card>
            </v-col>
            <v-col v-if="selectedPlay.action === 'UPD' && originalPlay" cols="6" class="py-0">
              <v-card
                flat
                v-if="selectedPlay.action === 'UPD' && originalPlay"
                class="mb-3"
                outlined>
                <v-card-title class="py-2 text-h6 font-weight-light">Original Play</v-card-title>
                <v-divider></v-divider>
                <v-card-text class="px-4 py-0">
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle>
                        Name
                      </v-list-item-subtitle>
                      <span :class="originalPlay.name !== selectedPlay.pending_state.name
                        ? 'red--text text-decoration-line-through' : 'grey--text'">
                        {{ originalPlay.name }}
                      </span>
                      <span
                        v-if="originalPlay.name !== selectedPlay.pending_state.name"
                        class="green--text">
                        {{ selectedPlay.pending_state.name }}
                      </span>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle>
                        Description
                      </v-list-item-subtitle>
                      <span
                        v-if="originalPlay.description !== selectedPlay.pending_state.description"
                        class="red--text text-decoration-line-through">
                        {{ originalPlay.description }}
                      </span>
                      <span
                        v-if="originalPlay.description !== selectedPlay.pending_state.description"
                        class="green--text">
                        {{ selectedPlay.pending_state.description }}
                      </span>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle>
                        CAPEC&trade; Attack Patterns
                      </v-list-item-subtitle>
                      <v-chip-group column>
                        <v-chip
                          v-if="compare.attackpatterns.length === 0"
                          label
                          outlined
                          small
                          color="grey">No Associated Attack Patterns</v-chip>
                        <v-chip
                          v-else
                          v-for="capec in compare.attackpatterns"
                          :key="`capec.${capec.val}`"
                          :ripple="false"
                          label
                          outlined
                          small
                          :color="capec.color"
                          :class="capec.color === 'red' ? 'text-decoration-line-through': ''">
                          {{ capec.val }}
                        </v-chip>
                      </v-chip-group>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle>
                        MITRE ATT&amp;CK&reg; Techniques
                      </v-list-item-subtitle>
                      <v-chip-group column>
                        <v-chip
                          v-if="compare.techniques.length === 0"
                          label
                          outlined
                          small
                          color="grey">No Associated Techniques</v-chip>
                        <v-chip
                          v-else
                          v-for="teq in compare.techniques"
                          :key="`teq.${teq.val}`"
                          :ripple="false"
                          label
                          outlined
                          small
                          :color="teq.color"
                          :class="teq.color === 'red' ? 'text-decoration-line-through': ''">
                          {{ teq.val }}
                        </v-chip>
                      </v-chip-group>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle>
                        NIST SP 800-53 (Rev. 5) Controls
                      </v-list-item-subtitle>
                      <v-chip-group column>
                        <v-chip
                          v-if="compare.controls.length === 0"
                          label
                          outlined
                          small
                          color="grey">No Associated NIST SP 800-53 Controls</v-chip>
                        <v-chip
                          v-else
                          v-for="ctrl in compare.controls"
                          :key="`ctrl.${ctrl.val}`"
                          :ripple="false"
                          label
                          outlined
                          small
                          :color="ctrl.color"
                          :class="ctrl.color === 'red' ? 'text-decoration-line-through': ''">
                          {{ ctrl.val }}
                        </v-chip>
                      </v-chip-group>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle>
                        CWE&trade; Weaknesses
                      </v-list-item-subtitle>
                      <v-chip-group column>
                        <v-chip
                          v-if="compare.cwes.length === 0"
                          label
                          outlined
                          small
                          color="grey">No Associated Weaknesses</v-chip>
                        <v-chip
                          v-else
                          v-for="cwe in compare.cwes"
                          :key="`cwe.${cwe.val}`"
                          :ripple="false"
                          label
                          outlined
                          small
                          :color="cwe.color"
                          :class="cwe.color === 'red' ? 'text-decoration-line-through': ''">
                          {{ cwe.val }}
                        </v-chip>
                      </v-chip-group>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle>
                        Tools
                        </v-list-item-subtitle>
                      <v-chip-group column>
                        <v-chip
                          v-if="compare.tools.length === 0"
                          label
                          outlined
                          small
                          color="grey">No Associated Tools</v-chip>
                        <v-chip
                          v-for="tool in compare.tools"
                          :key="`tool.${tool.val}`"
                          :ripple="false"
                          label
                          outlined
                          small
                          :color="tool.color"
                          :class="tool.color === 'red' ? 'text-decoration-line-through': ''">
                          {{ tool.val }}
                        </v-chip>
                      </v-chip-group>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle>
                        Tags
                      </v-list-item-subtitle>
                      <v-chip-group column>
                        <v-chip
                          v-if="compare.tags.length === 0"
                          label
                          outlined
                          small
                          color="grey">No Associated Tags</v-chip>
                        <v-chip
                          v-for="tag in compare.tags"
                          :key="`tag.${tag.val}`"
                          :ripple="false"
                          label
                          outlined
                          small
                          :color="tag.color"
                          :class="tag.color === 'red' ? 'text-decoration-line-through': ''">
                          {{ tag.val }}
                        </v-chip>
                      </v-chip-group>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle>
                        Objectives
                      </v-list-item-subtitle>
                      <v-chip-group column>
                        <v-chip
                          v-if="compare.objectives.length === 0"
                          label
                          outlined
                          small
                          color="grey">No Associated Objectives</v-chip>
                        <v-chip
                          v-for="obj in compare.objectives"
                          :key="`obj.${obj.val}`"
                          :ripple="false"
                          label
                          outlined
                          small
                          :color="obj.color"
                          :class="obj.color === 'red' ? 'text-decoration-line-through': ''">
                          {{ obj.val }}
                        </v-chip>
                      </v-chip-group>
                    </v-list-item-content>
                  </v-list-item>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          <v-row class="mt-12" no-gutters>
            <v-spacer></v-spacer>
            <v-btn
              type="button"
              :disabled="isBeingEdited"
              @click="approvePlay"
              color="primary"
              small
              text>
              Approve
              <v-icon small color="primary" class="ml-2" v-text="mdiCheckOutline" />
            </v-btn>
          </v-row>
        </v-form>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mdiCheckOutline, mdiPencilOutline, mdiCloseOutline } from '@mdi/js';
import { mapActions, mapGetters } from 'vuex';
import moderateTextField from '@/components/forms/reviewModerations/moderateTextField.vue';
import moderateTextArea from '@/components/forms/reviewModerations/moderateTextArea.vue';
import moderateSelectChips from '@/components/forms/reviewModerations/moderateSelectChips.vue';
import utils from '@/utils';

export default {
  name: 'reviewPlays',
  components: {
    moderateTextField,
    moderateTextArea,
    moderateSelectChips
  },
  data() {
    return {
      mdiCheckOutline,
      mdiPencilOutline,
      mdiCloseOutline,
      selectedPlay: undefined,
      formValid: false,
      originalPlay: undefined,
      isBeingEdited: false,
      compare: {
        techniques: [],
        controls: [],
        tools: [],
        tags: [],
        objectives: []
      }
    };
  },
  computed: {
    ...mapGetters([
      'pendingPlays',
      'capecOptions',
      'techniqueOptions',
      'controlOptions',
      'cweOptions',
      'toolOptions',
      'tagOptions',
      'objectiveOptions'
    ])
  },
  methods: {
    pendingState: utils.pendingState,
    ...mapActions([
      'moderatePlay',
      'fetchPending',
      'fetchOriginalByID',
      'fetchFilterOptions'
    ]),
    checkActive() {
      this.isBeingEdited = (this.$refs.modNameField && this.$refs.modNameField.editField)
        || (this.$refs.modUrlField && this.$refs.modUrlField.editField)
        || (this.$refs.modDescField && this.$refs.modDescField.editField);
    },
    async approvePlay() {
      try {
        await this.moderatePlay({
          ...this.selectedPlay,
          approved: true
        });
        this.selectedPlay = undefined;
        this.fetchPending();
        this.fetchFilterOptions();
      } catch (err) {
        console.error(err);
      }
    },
    async reviewPlay(play) {
      this.selectedPlay = play;
      if (this.selectedPlay.action === 'UPD') {
        this.originalPlay = await this.fetchOriginalByID(['play', this.selectedPlay.target]);
        this.compareEdits('attackpatterns', this.selectedPlay.pending_state.attackpatterns);
        this.compareEdits('techniques', this.selectedPlay.pending_state.techniques);
        this.compareEdits('controls', this.selectedPlay.pending_state.controls);
        this.compareEdits('cwes', this.selectedPlay.pending_state.cwes);
        this.compareEdits('tools', this.selectedPlay.pending_state.tools);
        this.compareEdits('tags', this.selectedPlay.pending_state.tags);
        this.compareEdits('objectives', this.selectedPlay.pending_state.objectives);
      }
    },
    compareEdits(field, ids) {
      let field2;
      let options = [];
      switch (field) {
        case 'attackpatterns':
          field2 = 'custom_id';
          options = [...this.capecOptions];
          break;
        case 'techniques':
          field2 = 'mitre_id';
          options = [...this.techniqueOptions];
          break;
        case 'controls':
          field2 = 'label';
          options = [...this.controlOptions];
          break;
        case 'cwes':
          field2 = 'custom_id';
          options = [...this.cweOptions];
          break;
        case 'objectives':
          field2 = 'title';
          options = [...this.objectiveOptions];
          break;
        case 'tools':
          field2 = 'name';
          options = [...this.toolOptions];
          break;
        default: // 'tags'
          field2 = 'name';
          options = [...this.tagOptions];
          break;
      }
      const orig = this.originalPlay[field].map((x) => {
        if (field === 'attackpatterns') return { id: x.id, custom_id: `CAPEC-${x.id}` };
        if (field === 'cwes') return { id: x.id, custom_id: `CWE-${x.id}` };
        return { id: x.id, [field2]: x[field2] };
      });
      const changed = ids.map((id) => {
        const isSelected = options.find((opt) => opt.id === id);
        return isSelected;
      });
      const all = [...orig, ...changed];
      let vals = all.map((z) => {
        const inOld = orig.find((x) => x.id === z.id);
        const inNew = changed.find((y) => y.id === z.id);
        let color = inOld === undefined ? 'green' : 'grey';
        color = inNew === undefined ? 'red' : color;
        return {
          val: z[field2],
          color
        };
      });
      vals = vals.reduce((acc, curr) => {
        const exists = acc.find((z) => (z.val === curr.val));
        if (exists === undefined) acc.push(curr);
        return acc;
      }, []);
      this.compare[field] = vals;
    },
    async editPlay(field, newVal) {
      const changed = {
        ...this.selectedPlay,
        pending_state: {
          ...this.selectedPlay.pending_state,
          [field]: newVal
        }
      };
      try {
        await this.moderatePlay(changed);
        this.fetchPending();
        this.reviewPlay(changed);
      } catch (err) {
        console.error(err);
      }
    }
  }
};
</script>
